// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built rubber (a ring or a band of rubber). The upstream rubber builds a ring-by-ring
// mesh from a spline. This one lays a tube along the smoothed outline. The drag point
// smoothing is the engine code, so the visible shape follows the physics shape.
// The tube centre is `height` above the playfield. rotX and rotY of the spec are ignored.

import { type BufferGeometry, CatmullRomCurve3, TubeGeometry, Vector3 } from "three";
import type { RubberSpec } from "../../shared/contracts.ts";
import { smoothLoop } from "./shared.ts";

/** Segments around the tube. */
const RADIAL_SEGMENTS = 10;
/** Points closer than this are one point. Equal points break the curve. */
const MIN_GAP = 0.01;

/** The geometry of a rubber and where to put it. */
export interface RubberGeometry {
  readonly geometry: BufferGeometry;
  /** Table position of the centre of the outline. The geometry is centred on it. */
  readonly center: { readonly x: number; readonly y: number };
}

/** Build the tube of a rubber. Returns null for an outline with fewer than three points. */
export function generateRubber(spec: RubberSpec): RubberGeometry | null {
  const raw = smoothLoop(spec.dragPoints);
  const points: Array<{ x: number; y: number }> = [];
  for (const p of raw) {
    const last = points[points.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > MIN_GAP) points.push({ x: p.x, y: p.y });
  }
  const first = points[0];
  const end = points[points.length - 1];
  if (first && end && points.length > 1 && Math.hypot(first.x - end.x, first.y - end.y) <= MIN_GAP)
    points.pop();
  if (points.length < 3) return null;

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  // Three frame: x, height, y. The tube centre is at height 0 here. The item group lifts it.
  const curve = new CatmullRomCurve3(
    points.map((p) => new Vector3(p.x - center.x, 0, p.y - center.y)),
    true,
    "centripetal",
  );
  const geometry = new TubeGeometry(
    curve,
    Math.max(24, points.length * 2),
    Math.max(spec.thickness, 0.5) / 2,
    RADIAL_SEGMENTS,
    true,
  );
  geometry.name = `rubber-${spec.name}`;
  return { geometry, center };
}
